import i18next from 'i18next';
import moment, {type Moment} from 'moment-timezone';

import {type SelectOption} from '@famly/mf_input_select';
import {FinancePeriod, PurchaseApprovalStatusType, type PurchaseInput} from 'web-app/util/graphql/api-types';
import {bifurcate} from 'web-app/util/array';
import {formatForIso8601, toMinutes} from 'web-app/react/components/form-elements/time/time';
import {hasValue} from '@famly/stat_ts-utils_has-value';
import {isEmptyString} from 'web-app/util/string';
import {PurchasePreviewConflictKind} from 'web-app/util/graphql/api-types';
import type {UnbookableSessionFragment} from 'web-app/react/bookings-finance/graphql/prevent-double-booking/fragments/__generated__/unbookable-sessions.fragment.api-types';
import type {NewPurchaseConflictMessageFragment} from 'web-app/react/bookings-finance/graphql/prevent-double-booking/fragments/__generated__/new-purchase-conflict-message.fragment.api-types';
import type {ConflictInfo} from 'web-app/react/profile/child-profile/plans-and-invoices/ad-hoc-purchases/purchase-modal/components/confirm-double-booking-modal';

import {
    type Product,
    type Session,
    type ProductVersion,
    type SessionVersion,
    type WithDay,
    type UpsertPurchaseInput,
    type ProductFormValue,
    type PurchaseProductBooking,
    type PurchaseSessionBooking,
    type SessionFormValue,
    isProductFormValue,
    type FormValue,
    type ChildPricingData,
} from './types';

export const getFilteredBookingTypePricings = (type: ProductVersion | SessionVersion) =>
    type.pricing.filter(pricing => pricing.volume === 1 && pricing.adHocPrice !== null);

export const isProductVersion = (version: ProductVersion | SessionVersion): version is ProductVersion =>
    version.__typename === 'ProductVersion';

export const isSession = (productOrSession: Product | Session): productOrSession is Session =>
    productOrSession.__typename === 'SessionVersion';

export const byPricingGroupAndDay =
    (pricingGroupId: string | null, selectedDay: string | null) =>
    <T extends ProductVersion | SessionVersion>(version: T) => {
        const isInPricingGroup = getFilteredBookingTypePricings(version).some(
            pricing => pricing.pricingGroupId === pricingGroupId,
        );

        if (!isInPricingGroup) {
            return false;
        }

        const availabilities = (
            isProductVersion(version) ? version.productAvailabilities : version.sessionAvailabilities
        ) as Array<WithDay>;

        return availabilities.some(availability => availability.day === selectedDay);
    };

export const toDomainModel =
    (pricingGroupId: string | null, selectedDay: string | null) =>
    <T extends ProductVersion | SessionVersion>(version: T): Product | Session => {
        const pricing = getFilteredBookingTypePricings(version).find(
            pricing => pricing.pricingGroupId === pricingGroupId,
        )!;

        const findByDay = (availability: WithDay) => availability.day === selectedDay;

        const availability = isProductVersion(version)
            ? version.productAvailabilities.find(findByDay)
            : version.sessionAvailabilities.find(findByDay);

        if (isProductVersion(version)) {
            return {
                ...version,
                pricing,
                availability: availability as Product['availability'],
            };
        }

        return {
            ...version,
            pricing,
            availability: availability as Session['availability'],
        };
    };

export const getDisplayAvailabilityForSession = (session: Session) => {
    const {availability} = session;

    if (availability === null) {
        return '';
    }

    const isFlexible = session.flexibleHours;

    if (isFlexible) {
        return i18next.t('session.flexible');
    }

    const from = moment(availability.timeRange.from, 'HH:mm:ss').format('LT');
    const to = moment(availability.timeRange.to, 'HH:mm:ss').format('LT');
    return `${from} - ${to}`;
};

export const calculateTotalPriceForItem = (formValue: FormValue): number => {
    const price = Number.parseFloat(
        hasValue(formValue.item.pricing.adHocPrice) && !isEmptyString(formValue.item.pricing.adHocPrice)
            ? formValue.item.pricing.adHocPrice
            : '0',
    );

    if (isSession(formValue.item)) {
        if (formValue.item.pricing.period === FinancePeriod.HOURLY && hasValue(formValue.range)) {
            return ((toMinutes(formValue.range.to) - toMinutes(formValue.range.from)) / 60) * price;
        }

        return price;
    }

    return price * (formValue.quantity ?? 1);
};

export const toPurchaseInputProductBooking = (product: ProductFormValue): PurchaseProductBooking => ({
    amount: product.quantity,
    bookedPrice: product.item.pricing.adHocPrice,
    pricingGroupId: product.item.pricing.pricingGroupId,
    productAvailabilityId: product.item.availability.productAvailabilityId,
    purchaseDate: product.date.format('YYYY-MM-DD'),
});

export const toPurchaseInputSessionBooking = (session: SessionFormValue): PurchaseSessionBooking => {
    return {
        bookedPrice: session.item.pricing.adHocPrice,
        pricingGroupId: session.item.pricing.pricingGroupId,
        purchaseDate: session.date.format('YYYY-MM-DD'),
        sessionAvailabilityId: session.item.availability.sessionAvailabilityId,
        range: session.item.flexibleHours
            ? {
                  from: formatForIso8601(session.range.from),
                  to: formatForIso8601(session.range.to),
              }
            : undefined,
    };
};

export const toPurchaseInputBody = ({
    childIds,
    items,
    discount,
    note,
    noteVisibleToParents,
    purchaseId,
    shouldApprove,
}: UpsertPurchaseInput): Array<PurchaseInput> => {
    const [products, sessions] = bifurcate<FormValue, ProductFormValue, SessionFormValue>(items, isProductFormValue);

    return childIds.map<PurchaseInput>(childId => ({
        purchaseId,
        childId,
        approve: shouldApprove ? PurchaseApprovalStatusType.Approved : undefined,
        discountAmount: discount?.toFixed(2) ?? '0',
        productBookings: products.map(toPurchaseInputProductBooking),
        sessionBookings: sessions.map(toPurchaseInputSessionBooking),
        note: isEmptyString(note) ? undefined : note,
        noteVisibleToParents: isEmptyString(note) ? undefined : noteVisibleToParents,
    }));
};

export const getPricingGroupIdFromRuleGroupIdOnDate = (
    ruleGroupId: string,
    selectedDate: Moment | null,
    childPricingData?: ChildPricingData,
) => {
    const ruleGroupPricings =
        childPricingData?.ruleGroups.find(ruleGroup => ruleGroup.ruleGroupId === ruleGroupId)?.pricing ?? [];

    if (ruleGroupPricings.length === 0) {
        return null;
    }

    const pricingGroup = ruleGroupPricings.find(pricing => {
        const pricingFrom = moment(pricing.dates.from);
        if (!hasValue(pricing.dates.to)) {
            return pricingFrom.isSameOrBefore(selectedDate);
        }

        const pricingTo = moment(pricing.dates.to);
        return selectedDate?.isBetween(pricingFrom, pricingTo, 'day', '[]');
    });

    return pricingGroup?.pricingGroupId ?? null;
};

export const isRuleGroupSelected = (ruleGroupOptions: Array<SelectOption>, selectedPricingGroupId?: string | null) =>
    ruleGroupOptions.some(ruleGroup => ruleGroup.value === selectedPricingGroupId);

export function getWarningToken({token}: {token: PurchasePreviewConflictKind}) {
    if (token === 'ALREADY_ON_PLAN') {
        return 'alreadyOnPlan';
    }
    if (token === 'SESSION_ON_PLAN') {
        return 'sessionOnPlan';
    }
    if (token === 'ALREADY_PURCHASED') {
        return 'alreadyPurchased';
    }
    if (token === 'ANOTHER_BOOKED_PURCHASE') {
        return 'anotherBookedPurchase';
    }
    if (token === 'ANOTHER_INPUT_PURCHASE') {
        return 'anotherInputPurchase';
    }

    return 'sessionOnPlan';
}

/**
 *
 * Since a session can have multiple conflicts at once and we want to only show one, we need to check and return the most important one
 * both UnbookableSession and NewPurchaseConflict have the same conflictKind that's why they are handled the same way
 *
 * Conflict priority (highest to lowest):
 * 1. ALREADY_ON_PLAN - The session/item is already included in an existing plan
 * 2. SESSION_ON_PLAN - A session that conflicts with an existing plan session
 * 3. ALREADY_PURCHASED - The session/item has already been purchased separately
 * 4. ANOTHER_BOOKED_PURCHASE - Conflicts with another confirmed booking/purchase
 * 5. ANOTHER_INPUT_PURCHASE - Conflicts with another purchase being made in the same form
 *
 * This prioritization ensures users see the most actionable and relevant conflict message
 * when multiple issues exist for the same session or purchase.
 *
 * @param purchaseConflict - Array of conflict objects (can be either NewPurchaseConflictMessageFragment or UnbookableSessionFragment)
 * @returns The highest priority conflict object, or null if no conflicts exist
 *
 */
export function getConflict<T extends NewPurchaseConflictMessageFragment | UnbookableSessionFragment>(
    purchaseConflict?: T[],
) {
    if (!purchaseConflict) {
        return null;
    }

    const ALREADY_ON_PLAN = purchaseConflict.find(
        conflict => conflict.conflictKind === PurchasePreviewConflictKind.ALREADY_ON_PLAN,
    );
    if (ALREADY_ON_PLAN) {
        return ALREADY_ON_PLAN;
    }

    const SESSION_ON_PLAN = purchaseConflict.find(
        conflict => conflict.conflictKind === PurchasePreviewConflictKind.SESSION_ON_PLAN,
    );
    if (SESSION_ON_PLAN) {
        return SESSION_ON_PLAN;
    }

    const ALREADY_PURCHASED = purchaseConflict.find(
        conflict => conflict.conflictKind === PurchasePreviewConflictKind.ALREADY_PURCHASED,
    );
    if (ALREADY_PURCHASED) {
        return ALREADY_PURCHASED;
    }

    const ANOTHER_BOOKED_PURCHASE = purchaseConflict.find(
        conflict => conflict.conflictKind === PurchasePreviewConflictKind.ANOTHER_BOOKED_PURCHASE,
    );
    if (ANOTHER_BOOKED_PURCHASE) {
        return ANOTHER_BOOKED_PURCHASE;
    }

    const ANOTHER_INPUT_PURCHASE = purchaseConflict.find(
        conflict => conflict.conflictKind === PurchasePreviewConflictKind.ANOTHER_INPUT_PURCHASE,
    );
    if (ANOTHER_INPUT_PURCHASE) {
        return ANOTHER_INPUT_PURCHASE;
    }
    return null;
}

/**
 * Extracts and formats session conflicts for display in the booking confirmation modal
 *
 * @param sessions - Array of sessions containing potential conflict information
 * @returns Array of formatted conflicts ready for display
 */
export const extractSessionConflicts = (sessions: Session[]): ConflictInfo[] => {
    const conflictsList: ConflictInfo[] = [];

    sessions.forEach(session => {
        if (session.sessionConflict && session.sessionConflict.length > 0) {
            session.sessionConflict.forEach(conflict => {
                conflictsList.push({
                    sessionId: session.sessionId,
                    sessionName: session.title,
                    date: conflict.date,
                    time: conflict.range
                        ? `${moment(conflict.range.from, 'HH:mm:ss').format('HH:mm')} - ${moment(
                              conflict.range.to,
                              'HH:mm:ss',
                          ).format('HH:mm')}`
                        : undefined,
                });
            });
        }
    });

    return conflictsList;
};
